import { GeoBounds, GeoDatasourceState } from '@/services/endpoints/geo';

export type GeoAvailabilityStatus =
  /** states not loaded yet */
  | 'loading'
  /** no selected list carries addresses: zone selection disabled, reason shown */
  | 'unavailable'
  /** some qualifying list is still being geocoded: wait for the next sweep */
  | 'processing'
  | 'ready';

export type GeoAvailability = {
  status: GeoAvailabilityStatus;
  /** Datasources that qualify, i.e. the ones the street list is read from. */
  qualifyingIds: string[];
  /** Citizens of every selected list, addresses or not. */
  total: number;
  /** Citizens the polygon can match (quality exact or interpolated). */
  onMap: number;
  /** Citizens the street list can match (onMap plus street-only matches). */
  withStreet: number;
  /** Citizens still waiting for geocoding across the qualifying lists. */
  pending: number;
  /** Union of the boxes of the qualifying lists, where the map opens; null when none has one. */
  bounds: GeoBounds | null;
};

const union = (a: GeoBounds | null, b: GeoBounds | null): GeoBounds | null =>
  a && b
    ? { latMin: Math.min(a.latMin, b.latMin), latMax: Math.max(a.latMax, b.latMax), lonMin: Math.min(a.lonMin, b.lonMin), lonMax: Math.max(a.lonMax, b.lonMax) }
    : a ?? b;

/**
 * Readiness of the zone selection for the selected lists (geolocation design, section 3.5):
 * enabled when at least one selected list qualifies and every qualifying list is ready. Counts
 * are per-list sums, indicative for a multi-list selection; the recap stays exact.
 */
export const geoAvailability = (states: GeoDatasourceState[] | undefined): GeoAvailability => {
  if (!states) return { status: 'loading', qualifyingIds: [], total: 0, onMap: 0, withStreet: 0, pending: 0, bounds: null };
  const qualifying = states.filter((s) => s.qualifies);
  const sum = (pick: (s: GeoDatasourceState) => number | null) => qualifying.reduce((acc, s) => acc + (pick(s) ?? 0), 0);
  const onMap = sum((s) => s.polygonEligible);
  return {
    status: qualifying.length === 0 ? 'unavailable' : qualifying.every((s) => s.ready) ? 'ready' : 'processing',
    qualifyingIds: qualifying.map((s) => s.datasourceId),
    total: states.reduce((acc, s) => acc + s.total, 0),
    onMap,
    withStreet: onMap + sum((s) => s.streetOnly),
    pending: sum((s) => s.pending),
    bounds: qualifying.reduce<GeoBounds | null>((acc, s) => union(acc, s.bounds), null),
  };
};
